//
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { getCurrentAccount, SocialIdType } from '@hcengineering/core'
import { getMetadata, getResource } from '@hcengineering/platform'
import {
  type AppAccessSubject,
  type ApplicationModes,
  type ApplicationsByRole,
  canSwitchMode,
  filterAppsForSubject,
  getAllowedPickedMode,
  getModeNames,
  isAppAllowedForSubject,
  isAppDocAllowedForSubject
} from './appAccess'
import { type Door, isDoorForSubject, mergeDoorsIntoModes, mergeDoorsIntoRoleTable, parseDoors } from './doors'
import { workbenchPlugin as plugin } from './plugin'

/**
 * The operator-editable role -> aliases table, as loaded from the branding file.
 *
 * @public
 */
export function getApplicationsByRoleTable (): ApplicationsByRole | undefined {
  // DCM doors (dcm.7): each door's alias joins the role lists it names. See ./doors.ts.
  return mergeDoorsIntoRoleTable(getMetadata(plugin.metadata.ApplicationsByRole), getDoors())
}

/**
 * The operator-editable per-person modes (`modes` + `modeByEmail`), as loaded from the branding
 * file. See ./appAccess.ts for how they combine with the role table.
 *
 * @public
 */
export function getApplicationModes (): ApplicationModes | undefined {
  // DCM doors (dcm.7): each door's alias joins the mode lists it names. See ./doors.ts.
  return mergeDoorsIntoModes(getMetadata(plugin.metadata.ApplicationModes), getDoors())
}

let doorsCache: { raw: unknown, doors: Door[] } | undefined

/**
 * DCM doors (dcm.7): the validated `doors` from branding.json, in file order. Re-parsed only when
 * the metadata object changes.
 *
 * @public
 */
export function getDoors (): Door[] {
  const raw = getMetadata(plugin.metadata.Doors)
  if (doorsCache === undefined || doorsCache.raw !== raw) doorsCache = { raw, doors: parseDoors(raw) }
  return doorsCache.doors
}

/**
 * The doors the signed-in account is shown: in its hard audience (`onlyFor`) and allowed by the
 * fence (modes, role table, the picked "View as" mode). `takenAliases` drops a door whose alias
 * a model application already uses -- the model wins.
 *
 * @public
 */
export function getDoorsForCurrentAccount (
  takenAliases: Iterable<string> = [],
  pickedMode: string | undefined = getPickedMode()
): Door[] {
  const taken = new Set(takenAliases)
  const subject = getCurrentAppAccessSubject()
  const doors = getDoors().filter((d) => !taken.has(d.alias) && isDoorForSubject(d, subject))
  return filterAppsForSubject(subject, getApplicationsByRoleTable(), getApplicationModes(), doors, pickedMode)
}

/**
 * Is this alias a door the signed-in account must not open (outside its `onlyFor`)? Used by the
 * workbench to send a deep link elsewhere, the same way a fenced application is.
 *
 * @public
 */
export function isDoorAliasOutsideAudience (alias: string | undefined): boolean {
  if (alias === undefined) return false
  const door = getDoors().find((d) => d.alias === alias)
  return door !== undefined && !isDoorForSubject(door, getCurrentAppAccessSubject())
}

/**
 * The signed-in account as the fence sees it: role in this workspace plus its e-mail addresses
 * (live e-mail social ids only).
 *
 * @public
 */
export function getCurrentAppAccessSubject (): AppAccessSubject | undefined {
  const account = getCurrentAccount()
  if (account == null) return undefined
  const emails = (account.fullSocialIds ?? [])
    .filter((it) => it.type === SocialIdType.EMAIL && it.isDeleted !== true)
    .map((it) => it.value)
  return { role: account.role, emails }
}

//
// DCM: the "View as" switch. The mode an admin picks for themselves lives in this browser's
// localStorage, keyed by account, so it survives a reload and never follows a shared browser
// to the next person. It is honoured only while canSwitchMode() says yes (see ./appAccess.ts),
// so a stale or hand-written entry cannot widen anybody's view.
//

const PICKED_MODE_KEY_PREFIX = 'pory.viewMode.'
const pickedModeListeners = new Set<(mode: string | undefined) => void>()

function pickedModeKey (): string | undefined {
  const uuid = getCurrentAccount()?.uuid
  if (uuid === undefined || uuid === null || uuid === '') return undefined
  return PICKED_MODE_KEY_PREFIX + uuid
}

/**
 * The mode the signed-in account picked for itself, if it may be honoured right now; otherwise
 * `undefined` (= the default: role table plus `modeByEmail`).
 *
 * @public
 */
export function getPickedMode (): string | undefined {
  const key = pickedModeKey()
  if (key === undefined) return undefined
  let stored: string | null = null
  try {
    stored = localStorage.getItem(key)
  } catch {
    return undefined
  }
  return getAllowedPickedMode(getCurrentAppAccessSubject(), getApplicationModes(), stored ?? undefined)
}

/**
 * Pick a mode for the signed-in account, or `undefined` to go back to the default. Refused
 * (returns false) for an account that may not switch or a mode that does not exist.
 *
 * @public
 */
export function setPickedMode (mode: string | undefined): boolean {
  const key = pickedModeKey()
  if (key === undefined) return false
  if (mode !== undefined && getAllowedPickedMode(getCurrentAppAccessSubject(), getApplicationModes(), mode) === undefined) {
    return false
  }
  try {
    if (mode === undefined) localStorage.removeItem(key)
    else localStorage.setItem(key, mode)
  } catch {
    return false
  }
  const now = getPickedMode()
  for (const listener of pickedModeListeners) listener(now)
  return true
}

/**
 * Be told when the signed-in account picks a mode. Returns the unsubscribe function. (A plain
 * callback rather than a Svelte store: this package has no svelte dependency of its own.)
 *
 * @public
 */
export function onPickedModeChange (listener: (mode: string | undefined) => void): () => void {
  pickedModeListeners.add(listener)
  return () => {
    pickedModeListeners.delete(listener)
  }
}

/** May the signed-in account use the "View as" switch? @public */
export function canCurrentAccountSwitchMode (): boolean {
  return canSwitchMode(getCurrentAppAccessSubject(), getApplicationModes())
}

/** The modes the "View as" switch offers, in branding.json order. @public */
export function getSwitchableModeNames (): string[] {
  return canCurrentAccountSwitchMode() ? getModeNames(getApplicationModes()) : []
}

/**
 * Is this application alias allowed for the signed-in account's role?
 *
 * UI fence only. It answers "should this account be shown a way in", never "may this account
 * read this data" -- that stays with space membership and role in the transactor. See
 * ./appAccess.ts.
 *
 * @public
 */
export function isAppAllowedForCurrentAccount (alias: string, pickedMode: string | undefined = getPickedMode()): boolean {
  return isAppAllowedForSubject(
    alias,
    getCurrentAppAccessSubject(),
    getApplicationsByRoleTable(),
    getApplicationModes(),
    pickedMode
  )
}

/**
 * Narrow a list of applications to those the signed-in account's role is shown.
 *
 * Can only remove. Callers pass a list that the accessLevel floor, the excluded-applications
 * list and the per-workspace guest fence have already narrowed; this never puts anything back.
 *
 * @public
 */
export function filterAppsForCurrentAccount<T extends { alias: string }> (
  apps: T[],
  pickedMode: string | undefined = getPickedMode()
): T[] {
  return filterAppsForSubject(
    getCurrentAppAccessSubject(),
    getApplicationsByRoleTable(),
    getApplicationModes(),
    apps,
    pickedMode
  )
}

/**
 * The alias-site form of the same question, resolved against the application document.
 *
 * 🔴 `hidden: true` applications are NEVER fenced by the table, and that is load-bearing.
 * They are not rail entries -- `setting`, `inbox` and `notification` are declared hidden and
 * are reached from the account menu and the top of the rail, not from the application list --
 * so they are not in the set the table is written about. An operator writing an explicit
 * allow-list for USER is enumerating the applications they want in the rail; they are not
 * saying "and take Settings away". Fencing hidden applications would lock every constrained
 * role out of their own settings the moment a table appears in branding.json.
 *
 * Passing `undefined` (an alias with no application behind it) allows: an unknown alias is
 * not this fence's business.
 *
 * @public
 */
export function isApplicationAllowedForCurrentAccount (app: { alias: string, hidden?: boolean } | undefined): boolean {
  return isAppDocAllowedForSubject(
    app,
    getCurrentAppAccessSubject(),
    getApplicationsByRoleTable(),
    getApplicationModes(),
    getPickedMode()
  )
}

/** @public */
export async function logIn (loginInfo: { account: string, token?: string }): Promise<void> {
  const res = await getResource(plugin.function.LogIn)
  await res(loginInfo)
}

/** @public */
export async function logOut (): Promise<void> {
  const res = await getResource(plugin.function.LogOut)
  await res()
}
